/**
 * `/orders/requests/:id` — one purchase request, top to bottom in the order
 * the customer needs it: current status & next step, the vehicle, purchase
 * progress, price & payment, contact, history.
 *
 * Only the caller's own request is returned by the BFF (404 otherwise); staff
 * notes and other customers' data never reach this page.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, CardTitle, ErrorState, Spinner } from '../components/ui';
import { StockImage } from '../components/stock/StockImage';
import { PurchaseRequestStatusPill } from '../components/purchase-requests/PurchaseRequestCard';
import { linkButtonStyle } from '../components/purchase-requests/linkButtonStyle';
import { PortalApiError } from '../lib/apiClient';
import { getStockVehicleUrl } from '../lib/featureFlags';
import { getPurchaseRequest, type PurchaseRequestDetail } from '../lib/purchaseRequestsApi';
import {
  PURCHASE_STEPS,
  customerNotes,
  formatJpy,
  formatRequestDate,
  formatRequestDateTime,
  purchaseRequestStatus,
  rejectReasonText,
  requestNumber,
  vehicleTitle,
} from '../lib/purchaseRequestStatus';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'error'; code: string }
  | { kind: 'success'; request: PurchaseRequestDetail };

export function PurchaseRequestDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const numericId = Number(id);
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!Number.isInteger(numericId) || numericId <= 0) {
      setLoad({ kind: 'not_found' });
      return;
    }
    const ctrl = new AbortController();
    setLoad({ kind: 'loading' });
    getPurchaseRequest(numericId, ctrl.signal)
      .then((request) => setLoad({ kind: 'success', request }))
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof PortalApiError && err.status === 404) {
          setLoad({ kind: 'not_found' });
          return;
        }
        setLoad({ kind: 'error', code: err instanceof PortalApiError ? err.code : 'UNKNOWN' });
      });
    return () => ctrl.abort();
  }, [numericId]);

  return (
    <section data-testid="purchase-request-detail-page" style={{ display: 'grid', gap: '1.25rem' }}>
      <p style={{ margin: 0, fontSize: '0.875rem' }}>
        <Link to="/orders" style={{ color: '#1f4ed8', fontWeight: 600, textDecoration: 'none' }}>
          ← Back to order status
        </Link>
      </p>

      {load.kind === 'loading' ? (
        <div aria-busy="true" data-testid="purchase-request-loading">
          <Spinner block label="Loading" />
        </div>
      ) : null}

      {load.kind === 'not_found' ? (
        <div data-testid="purchase-request-not-found">
          <ErrorState
            title="Purchase request not found"
            description="It may have been removed, or it belongs to another account."
          />
        </div>
      ) : null}

      {load.kind === 'error' ? (
        <div data-testid="purchase-request-error">
          <ErrorState
            title="Failed to load"
            description={`Error code: ${load.code}`}
            onRetry={() => window.location.reload()}
          />
        </div>
      ) : null}

      {load.kind === 'success' ? <Detail request={load.request} /> : null}
    </section>
  );
}

function Detail({ request }: { request: PurchaseRequestDetail }): JSX.Element {
  const view = purchaseRequestStatus(request.state);
  const title = vehicleTitle(request);
  const ref = requestNumber(request.buyRequestId);
  const reason = request.state === 'rejected' ? rejectReasonText(request.rejectReason) : null;
  const notes = customerNotes(request.notes);
  const stockUrl =
    request.source === 'aucnet_stock' && request.stockVehicleId
      ? getStockVehicleUrl(request.stockVehicleId)
      : null;
  const supportHref = `/support?subject=${encodeURIComponent(`Purchase request ${ref} — ${title}`)}`;
  const requestedPrice = request.offerAmount ?? request.vehiclePriceSnapshot;

  return (
    <>
      <header>
        <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
          {ref} · Requested {formatRequestDate(request.createdAt)}
        </p>
        <h1
          style={{ margin: '0.125rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}
        >
          {title}
        </h1>
      </header>

      {/* 1. Current status & next step */}
      <Card aria-label="Current status">
        <CardBody>
          <div
            data-testid="purchase-request-current"
            style={{
              display: 'grid',
              gap: '0.625rem',
              borderLeft: `4px solid ${view.group === 'action' ? '#f59e0b' : view.outcome ? '#9ca3af' : '#1f4ed8'}`,
              paddingLeft: '0.875rem',
            }}
          >
            <div>
              <PurchaseRequestStatusPill state={request.state} />
            </div>
            <p style={{ margin: 0, fontSize: '0.9375rem', color: '#111827' }}>{view.description}</p>
            {reason ? (
              <p
                data-testid="purchase-request-reason"
                style={{ margin: 0, fontSize: '0.875rem', color: '#991b1b' }}
              >
                Reason: {reason}
              </p>
            ) : null}
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#111827' }}>
              <strong>Next step: </strong>
              {view.nextAction}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {view.group === 'action' ? (
                <Link to="/invoices" style={linkButtonStyle('primary')}>
                  Check invoice
                </Link>
              ) : null}
              <Link to={supportHref} style={linkButtonStyle('secondary')}>
                Ask about this request
              </Link>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* 2. Vehicle */}
      <Section title="Vehicle">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 16rem), 1fr))',
            gap: '1rem',
          }}
        >
          <StockImage
            src={request.vehicleImageUrlSnapshot ?? ''}
            alt={title}
            priority
            style={{
              width: '100%',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              borderRadius: '0.375rem',
              display: 'block',
            }}
          />
          <dl
            style={{
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'max-content 1fr',
              gap: '0.375rem 1rem',
              fontSize: '0.875rem',
              alignContent: 'start',
            }}
          >
            <Row label="Vehicle" value={title} />
            <Row label="Grade" value={request.vehicleGradeSnapshot ?? '—'} />
            <Row label="Auction grade" value={request.vehicleAuctionGradeSnapshot ?? '—'} />
            <Row label="Stock No." value={request.vehicleInventoryNoSnapshot ?? '—'} />
            {stockUrl ? (
              <Row
                label="Listing"
                value={
                  <a href={stockUrl} style={{ color: '#1f4ed8', fontWeight: 600 }}>
                    View on AUCNET STOCK
                  </a>
                }
              />
            ) : null}
          </dl>
        </div>
      </Section>

      {/* 3. Progress */}
      <Section title="Purchase progress">
        <ol
          data-testid="purchase-request-steps"
          style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.75rem' }}
        >
          {PURCHASE_STEPS.map((step, i) => {
            const n = i + 1;
            const ended = view.outcome !== null;
            let status: 'done' | 'current' | 'upcoming' | 'stopped';
            if (ended) status = n < view.currentStep ? (n <= 2 ? 'done' : 'stopped') : 'current';
            else
              status =
                n < view.currentStep ? 'done' : n === view.currentStep ? 'current' : 'upcoming';
            const resultText =
              n === 5 && view.outcome === 'not_purchased'
                ? 'Not purchased.'
                : n === 5 && view.outcome === 'withdrawn'
                  ? 'Request withdrawn.'
                  : step.description;
            const dot =
              status === 'done'
                ? '#059669'
                : status === 'current'
                  ? ended
                    ? '#9ca3af'
                    : '#1f4ed8'
                  : '#d1d5db';
            return (
              <li
                key={step.title}
                data-step-status={status}
                aria-current={status === 'current' ? 'step' : undefined}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.75rem 1fr',
                  gap: '0.625rem',
                  alignItems: 'start',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: '1.75rem',
                    height: '1.75rem',
                    borderRadius: '9999px',
                    background: dot,
                    color: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {status === 'done' ? '✓' : n}
                </span>
                <div>
                  <div
                    style={{
                      fontSize: '0.875rem',
                      fontWeight: status === 'current' ? 700 : 600,
                      color: status === 'upcoming' || status === 'stopped' ? '#6b7280' : '#111827',
                    }}
                  >
                    {step.title}
                    {status === 'current' && !ended ? ' — in progress' : ''}
                    {status === 'stopped' ? ' — not reached' : ''}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: '#4b5563' }}>{resultText}</div>
                </div>
              </li>
            );
          })}
        </ol>
      </Section>

      {/* 4. Price & payment */}
      <Section title="Price & payment">
        <dl
          style={{
            margin: 0,
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            gap: '0.375rem 1rem',
            fontSize: '0.875rem',
          }}
        >
          <Row
            label={request.offerAmount != null ? 'Your offer' : 'Price when requested'}
            value={<span data-testid="purchase-request-price">{formatJpy(requestedPrice)}</span>}
          />
          <Row
            label="Invoice, paid & balance"
            value="Shown on your invoice once the purchase terms are confirmed."
          />
        </dl>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.8125rem' }}>
          <Link
            to="/invoices"
            style={{ color: '#1f4ed8', fontWeight: 600, textDecoration: 'none' }}
          >
            Go to invoices →
          </Link>
        </p>
      </Section>

      {/* 5. Contact */}
      <Section title="Contact">
        <p style={{ margin: 0, fontSize: '0.875rem', color: '#111827' }}>
          Your Provide Cars contact will email you about this request. For questions, send us a
          message and mention {ref}.
        </p>
        <p style={{ margin: '0.75rem 0 0' }}>
          <Link to={supportHref} style={linkButtonStyle('secondary')}>
            Ask about this request
          </Link>
        </p>
        {notes ? (
          <p
            data-testid="purchase-request-notes"
            style={{ margin: '0.75rem 0 0', fontSize: '0.8125rem', color: '#4b5563' }}
          >
            Your note: {notes}
          </p>
        ) : null}
      </Section>

      {/* 6. History */}
      <Section title="History">
        <ul
          data-testid="purchase-request-history"
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'grid',
            gap: '0.5rem',
            fontSize: '0.875rem',
          }}
        >
          <li>
            <span style={{ color: '#6b7280' }}>{formatRequestDateTime(request.createdAt)}</span> —
            Purchase request submitted
          </li>
          {request.state !== 'submitted' && request.stateUpdatedAt ? (
            <li>
              <span style={{ color: '#6b7280' }}>
                {formatRequestDateTime(request.stateUpdatedAt)}
              </span>{' '}
              — {view.label}
            </li>
          ) : null}
        </ul>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <Card aria-label={title}>
      <CardHeader>
        <CardTitle level={2}>{title}</CardTitle>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <>
      <dt style={{ color: '#6b7280' }}>{label}</dt>
      <dd style={{ margin: 0, color: '#111827' }}>{value}</dd>
    </>
  );
}
